import { Component, Event, EventEmitter, h, Host, Prop, Element, State } from '@stencil/core';
import warn from '../../capabilities/warn';
import { ChangeEventDetail } from '../../events/ChangeEventDetail';
import { ChangeEventEmitter } from '../../events/ChangeEventEmitter';
import uid from '../../capabilities/identity/uid';

/**
 * hp6-pagination-control
 *
 * @description Contol pagination of paginated items
 * @use
 * @since 5.12.0
 * @version 8
 */
@Component({
  tag: 'hp6-pagination-control',
  styleUrl: 'pagination-control.scss',
  shadow: false, // No shadow DOM as this component has no slots and needs to be accessible from the parent component
})
export class PaginationControl implements ChangeEventEmitter {
  /**
   * The size of the label text. "xsmall Heading" or "small Heading"
   * - xxsmall heading label uses xsmall body for selection options
   * - xsmall heading label uses small body for selection options
   * - small heading label uses medium body for selection options
   * - medium heading label uses large body for selection options
   */
  @Prop() labelSizeSelection: 'xxs' | 'xs' | 'sm' | 'md' = 'xs';

  /**
   * The text that will be the label for the text-select
   */
  @Prop() pageText: string = 'Page';

  /**
   * The word that will show up between this.pageCurrent and this.pageCount
   * Page 42 of 69
   * {pageText} {pageCurrent} {pageOfText} {pageCount}
   */
  @Prop() pageOfText: string = 'of';

  /**
   * The total number of pages to be paginated
   */
  @Prop() pageCount: number;

  /**
   * Read/write prop used to keep track of current page
   */
  @Prop({ mutable: true, reflect: true }) pageCurrent: number = 1;

  /**
   * Will be passed along to the hp-text-select and hp-button ally-controls
   * props and will end up being the aria-controls attribute of the <button />
   * and <select /> tags.
   */
  @Prop() allyControls: string;

  /**
   * Will be the a11y label on the previous page button
   */
  @Prop() allyLabelTextPagePrevious: string = 'previous page';

  /**
   * Will be the a11y label on the next page button
   */
  @Prop() allyLabelTextPageNext: string = 'next page';

  /**
   * Emitted when an action happens
   */

  /**
   * ref to the <Host /> element
   */
  @Element() host: HTMLElement;

  @Event() hp6Change: EventEmitter<ChangeEventDetail>;

  @State() selectId = `hp6-pagination-control-select-${uid()}`;

  /**
   * action()
   *
   * @param event Event
   * @param page number
   */
  async buttonClick(event: Event, page: number) {
    this.setPage(event, page);
  }

  /**
   * Set this.pageCurrent and emit event
   *
   * @param event Event
   * @param page number
   * @returns void
   */
  async setPage(event: Event, page: number) {
    if (page < 0) {
      warn(`hp6-pagination-control - setPage(page) - page argument less than 0`);
      return;
    }

    if (page > this.pageCount) {
      warn(
        `hp6-pagination-control - setPage(page) - page argument greater than pageCount - page: ${page} - pageCount: ${this.pageCount}`,
      );
      return;
    }

    this.pageCurrent = page;
    this.emitEvent({ event, page: this.pageCurrent });
  }

  /**
   * onChange()
   *
   * @param event Event
   */
  onChange(event: Event) {
    // target is type HTMLSelectElement
    const target = event.target as HTMLSelectElement;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { value } = target;

    // value will be a string
    const page = parseInt(value, 10);

    this.setPage(event, page);
  }

  /**
   * emitEvent()
   *
   * @param page number
   */
  emitEvent({ event, page }) {
    this.hp6Change.emit(new ChangeEventDetail({ originalEvent: event, value: page }));
  }

  /**
   * constructor()
   */
  constructor() {
    this.buttonClick = this.buttonClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.emitEvent = this.emitEvent.bind(this);
  }

  componentWillLoad() {
    const { id } = this.host;
    if (id) {
      this.selectId = `${id}-page-select`;
    }
  }

  /**
   * render()
   *
   * @returns tsx
   */
  render() {
    const {
      pageText,
      pageOfText,
      labelSizeSelection,
      pageCount,
      pageCurrent,
      allyControls,
      allyLabelTextPagePrevious,
      allyLabelTextPageNext,
      selectId,
    } = this;

    const options = {};

    for (let i = 1; i <= pageCount; i++) {
      const option = {
        value: i,
        label: `${i} ${pageOfText} ${pageCount}`,
        selected: pageCurrent === i,
      };
      options[i] = option;
    }

    let buttonAriaAttributes = {};

    if (allyControls) {
      buttonAriaAttributes = {
        ...buttonAriaAttributes,
        'aria-controls': allyControls,
      };
    }

    return (
      <Host>
        <hp6-horizontal-rule />
        <nav aria-label="pagination">
          <div class="pagination-control-row">
            <button
              class={`pagination-control-button ${pageCurrent === 1 ? 'hidden' : ''}`}
              aria-label={allyLabelTextPagePrevious}
              onClick={event => {
                this.buttonClick(event, this.pageCurrent - 1);
              }}
              {...buttonAriaAttributes}
            >
              <hp6-icon name="system-arrow-left" sizeSelection="sm" />
            </button>

            {pageCount > 1 && (
              <hp6-text-select
                label-size-selection={labelSizeSelection}
                labelText={pageText}
                selected-index={pageCurrent - 1}
                {...buttonAriaAttributes}
              >
                <select id={selectId} onChange={this.onChange}>
                  {Object.keys(options).map(index => {
                    const option = options[index];
                    const { value, label, selected } = option;

                    return (
                      <option value={value} selected={selected}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </hp6-text-select>
            )}

            {pageCount <= 1 && (
              <div class={`single-page ${labelSizeSelection}`}>
                <div class="single-page-label">{pageText}</div>
                <div class="single-page-one">
                  1
                  {pageOfText}
                  {' '}
                  1
                </div>
              </div>
            )}

            <button
              class={`pagination-control-button ${pageCurrent === pageCount ? 'hidden' : ''}`}
              aria-label={allyLabelTextPageNext}
              onClick={event => {
                this.buttonClick(event, this.pageCurrent + 1);
              }}
              {...buttonAriaAttributes}
            >
              <hp6-icon name="system-arrow-right" sizeSelection="sm" />
            </button>
          </div>
        </nav>
        <hp6-horizontal-rule />
      </Host>
    );
  }
}
